import React from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ADMIN_CONTENT_TOOLS,
  type AdminContentToolValue,
  resolveAdminContentTool,
} from 'components/layout/adminNavigation';
import UploadMedia from 'pages/Admin/UploadMedia';
import QuestionLibrary from 'pages/Admin/QuestionLibrary';
import DailyPulseQuestions from 'pages/Admin/DailyPulseQuestions';
import FlowBuilder from 'pages/Admin/FlowBuilder';
import FlowData from 'pages/Admin/FlowData';
import CourseBuilder from 'pages/Admin/CourseBuilder';
import CourseData from 'pages/Admin/CourseData';
import QuizTools from 'pages/Admin/QuizTools';
import InspireCardsAdmin from 'pages/Admin/InspireCardsAdmin';

// Tool navigation lives in the admin sidebar (`components/layout/adminNavigation.ts`).
const CONTENT_TOOL_RENDERERS: Record<AdminContentToolValue, () => React.ReactNode> = {
  'upload-media': () => <UploadMedia />,
  'question-library': () => <QuestionLibrary />,
  'daily-pulse-questions': () => <DailyPulseQuestions />,
  'flow-builder': () => <FlowBuilder />,
  'flow-data': () => <FlowData />,
  'course-builder': () => <CourseBuilder />,
  'course-data': () => <CourseData />,
  'quiz-qa': () => <QuizTools embedded />,
  'inspire-cards': () => <InspireCardsAdmin />,
};

const ContentTools: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTool = resolveAdminContentTool(tabParam);
  const activeLabel = ADMIN_CONTENT_TOOLS.find((tool) => tool.value === activeTool)?.label;

  // Switching tools only changes `?tab=`, which ThemedApp's pathname-based scroll reset doesn't see.
  const previousToolRef = React.useRef(activeTool);
  React.useEffect(() => {
    if (previousToolRef.current === activeTool) {
      return;
    }
    previousToolRef.current = activeTool;
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.scrollTop = 0;
    }
  }, [activeTool]);

  React.useEffect(() => {
    if (tabParam !== 'roleplay-builder') {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'flow-builder');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, tabParam]);

  return (
    <section className="space-y-6 px-6">
      <div className="space-y-1">
        <p className="text-sm font-medium text-muted-foreground">Content</p>
        <h1 className="text-3xl font-semibold text-foreground" data-testid="content-tool-title">
          {activeLabel}
        </h1>
      </div>

      <div key={activeTool} data-testid={`content-tool-${activeTool}`}>
        {CONTENT_TOOL_RENDERERS[activeTool]()}
      </div>
    </section>
  );
};

export default ContentTools;
